import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Sun, Cloud, CloudSun, CloudRain, CloudDrizzle, CloudSnow, CloudLightning, CloudFog, Loader2, MapPin, Droplets,
} from "lucide-react";

// WMO weather code -> { Icon, label, colour }
const WMO = (code) => {
  const c = Number(code);
  if (c === 0) return { Icon: Sun, label: "Clear", cls: "text-amber-500" };
  if (c === 1 || c === 2) return { Icon: CloudSun, label: "Partly cloudy", cls: "text-amber-400" };
  if (c === 3) return { Icon: Cloud, label: "Overcast", cls: "text-slate-400" };
  if (c === 45 || c === 48) return { Icon: CloudFog, label: "Fog", cls: "text-slate-400" };
  if (c >= 51 && c <= 57) return { Icon: CloudDrizzle, label: "Drizzle", cls: "text-sky-400" };
  if ((c >= 61 && c <= 67) || (c >= 80 && c <= 82)) return { Icon: CloudRain, label: "Rain", cls: "text-sky-500" };
  if ((c >= 71 && c <= 77) || (c >= 85 && c <= 86)) return { Icon: CloudSnow, label: "Snow", cls: "text-sky-300" };
  if (c >= 95) return { Icon: CloudLightning, label: "Thunderstorm", cls: "text-violet-500" };
  return { Icon: Cloud, label: "—", cls: "text-slate-400" };
};

const dayName = (d, i) => (i === 0 ? "Today" : new Date(d + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short" }));
const t = (v, unit) => (v == null ? "—" : `${Math.round(v)}${unit || "°"}`);

export const WeatherCard = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard/weather").then((r) => setData(r.data)).catch(() => setData({ status: "unavailable" }));
  }, []);

  if (data === null) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="weather-card">
        <div className="flex items-center gap-2 text-slate-400 text-sm py-6"><Loader2 className="w-4 h-4 animate-spin" /> Loading forecast…</div>
      </div>
    );
  }

  const Header = () => (
    <h2 className="text-base font-bold text-slate-900 flex items-center gap-2"><CloudSun className="w-4 h-4 text-sky-500" /> Weather</h2>
  );

  if (data.status === "not_configured") {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="weather-card">
        <Header />
        <p className="text-sm text-slate-500 mt-2" data-testid="weather-not-configured">
          Set your company town to see a 7-day forecast here.
        </p>
        <Link to="/platform/onboarding" className="inline-block mt-3 text-sm font-semibold text-indigo-600 hover:text-indigo-700">Set company address →</Link>
      </div>
    );
  }
  if (data.status !== "ok" || !(data.days || []).length) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="weather-card">
        <Header />
        <p className="text-sm text-slate-500 mt-2" data-testid="weather-unavailable">
          {data.status === "unknown_location" ? `Couldn't locate “${data.query}”.` : "Forecast is temporarily unavailable."}
        </p>
      </div>
    );
  }

  const loc = data.location || {};
  const locLabel = [loc.name, loc.country].filter(Boolean).join(", ");
  const today = data.days[0];
  const tw = WMO(today.code);

  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="weather-card">
      <div className="flex items-start justify-between gap-2">
        <Header />
        <span className="inline-flex items-center gap-1 text-xs text-slate-500" data-testid="weather-location"><MapPin className="w-3.5 h-3.5" /> {locLabel}</span>
      </div>

      <div className="flex items-center gap-4 mt-3 mb-4">
        <tw.Icon className={`w-12 h-12 ${tw.cls}`} strokeWidth={1.5} />
        <div>
          <div className="text-3xl font-bold text-slate-900 tabular-nums">{t(today.tmax, data.unit)}</div>
          <div className="text-xs text-slate-500">{tw.label} · low {t(today.tmin, data.unit)}</div>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1" data-testid="weather-forecast">
        {data.days.map((d, i) => {
          const w = WMO(d.code);
          return (
            <div key={d.date} className="flex flex-col items-center rounded-lg py-2 hover:bg-slate-50" data-testid={`weather-day-${i}`}>
              <span className="text-[10px] font-semibold text-slate-500">{dayName(d.date, i)}</span>
              <w.Icon className={`w-5 h-5 my-1 ${w.cls}`} strokeWidth={2} />
              <span className="text-[11px] font-bold text-slate-800 tabular-nums">{t(d.tmax, "")}</span>
              <span className="text-[10px] text-slate-400 tabular-nums">{t(d.tmin, "")}</span>
              {d.precip != null && d.precip > 0 && (
                <span className="mt-0.5 inline-flex items-center gap-0.5 text-[9px] text-sky-500"><Droplets className="w-2.5 h-2.5" />{d.precip}%</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
